import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import ModalShell from "./ModalShell";

export default function UpdateEmailModal({
  open,
  initialEmail,
  onClose,
  onSend, // optional callback after success
  sending, // optional external flag (we'll OR it with internal)
  endpoint, // ✅ proxy endpoint url (same one you used in LeadGateModal)
  storedEmail, // ✅ OLD email (from localStorage / unlock)
  calculatorName,
  calculatorYear,
  pageUrl,
  resultsJson,
  inputsJson,
  pdf_notes
}) {
  const [email, setEmail] = useState(initialEmail || "");
  const [err, setErr] = useState("");
  const [localSending, setLocalSending] = useState(false);

  const isBusy = Boolean(sending) || localSending;

  // ---------------- Storage ----------------
  const UNK_GATE_KEY = "unk_calc_unlock_v1";
  const UNK_GATE_TTL_MS = 24 * 60 * 60 * 1000;
    const params = useMemo(() => new URLSearchParams(window.location.search), []);
     const qa = useMemo(
        () => (params.has("qa_mode")),
        [params]
      );

  const writeUnlock = (newEmail) => {
    const payload = {
      expiresAt: Date.now() + UNK_GATE_TTL_MS,
      email: String(newEmail || "").trim().toLowerCase(),
    };
    localStorage.setItem(UNK_GATE_KEY, JSON.stringify(payload));
  };

  useEffect(() => {
    if (!open) return;
    setEmail(initialEmail || "");
    setErr("");
    setLocalSending(false);
  }, [open, initialEmail]);

  const isValidEmail = (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

  // proxy helper (same pattern as your other modal)
  const proxyPost = async (path, data) => {
    const resp = await axios.post(
      endpoint,
      { path, method: "POST", raw: true, data, qa_mode: qa },
      { headers: { "Content-Type": "application/json" }, timeout: 30000 }
    );

    // your proxy may return success:false
    if (resp?.data?.success === false) {
      throw new Error(resp?.data?.message || "Request failed");
    }

    return resp?.data;
  };

  const submit = async () => {
    setErr("");

    const newEmail = String(email || "").trim().toLowerCase();
    const oldEmail = String(storedEmail || "").trim().toLowerCase();

    if (!oldEmail) return setErr("Old email is missing. Please reopen the results modal.");
    if (!newEmail) return setErr("Email Address is required.");
    if (!isValidEmail(newEmail)) return setErr("Please enter a valid email address.");
    if (oldEmail === newEmail) return setErr("Please enter a different email address.");

    // build results payload for email send
    const basePayload = {
      email: newEmail,
      calculator_name: calculatorName,
      calculator_year: calculatorYear,
      page_url: pageUrl,
      timestamp_utc: new Date().toISOString(),
      results_json: resultsJson,
      inputs_json: inputsJson,
      schema_version: 1,
    };

    setLocalSending(true);

    try {
      // 1) Update GHL email
      await proxyPost("/public/update-contact-email-ghl", {
        oldEmail,
        newEmail,
      });

      // 2) Update local storage (unlock)
      writeUnlock(newEmail);

      // 3) Send results email to NEW email
      await proxyPost("/public/send-results", {
        ...basePayload,
        action: "send_results_email",
        pdf_notes: pdf_notes || {},
      });

      // optional notify parent
      onSend?.(newEmail);

      // close modal
      onClose?.();
    } catch (e) {
      console.error("UpdateEmailModal error:", e);
      setErr(e?.message || "Something went wrong. Please try again.");
    } finally {
      setLocalSending(false);
    }
  };

  return (
    <ModalShell open={open} onClose={onClose} width={640}>
      <div className="p-5 bg-white text-center">
        <div
          style={{
            width: 66,
            height: 66,
            borderRadius: 999,
            background: "#e9f8ef",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 32,
            color: "#0f8a4c",
          }}
          aria-hidden="true"
        >
          ✉
        </div>

        <div className="mt-3 fw-bold" style={{ fontSize: 26 }}>
          Update Email Address
        </div>

        <div className="mt-2 text-muted" style={{ fontSize: 16 }}>
          Enter a different email address to receive these results.
        </div>

        <div className="mt-4 text-start">
          <label className="form-label fw-semibold">Email Address</label>
          <input
            className="form-control"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isBusy}
            style={{
              borderRadius: 12,
              padding: "12px 14px",
            }}
          />
          {err && (
            <div className="mt-2">
              <small className="text-danger fw-semibold">{err}</small>
            </div>
          )}
        </div>

        <button
          type="button"
          className="btn fw-semibold mt-4 w-100"
          onClick={submit}
          disabled={isBusy}
          style={{
            background: "#0f8a4c",
            borderColor: "#0f8a4c",
            color: "white",
            padding: "14px 16px",
            borderRadius: 14,
            fontSize: 16,
          }}
        >
          {isBusy ? "Sending..." : "Update & Send Results"}
        </button>

        <button
          type="button"
          className="btn btn-link mt-3 fw-semibold"
          onClick={onClose}
          disabled={isBusy}
          style={{ textDecoration: "none" }}
        >
          Cancel
        </button>
      </div>
    </ModalShell>
  );
}
